import { createRouter, createWebHistory } from "vue-router";
import { GoogleRecaptchaHelper } from "@/helpers/grecaptcha.helper";
import { FeatureFlagId, useFeatureFlagStore } from "@/stores/featureFlag.store";
import {
  usePermissionStore,
  type PermissionConfig,
  type PermissionConfigWithOperator
} from "@/stores/permission.store";

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: "/about",
      name: "About",
      component: () => import("../views/AboutView.vue"),
      meta: { requiresRecaptcha: true }
    },
    {
      path: "/home",
      name: "Home",
      component: () => import("../views/HomeView.vue")
    },
    {
      path: "/pricing",
      name: "Pricing",
      meta: {
        permission: {
          operation: "read",
          resource: "pricing"
        }
      },
      component: () => import("../views/PricingView.vue")
    },
    {
      path: "/pricing/:id",
      name: "PricingDetails",
      meta: {
        permission: {
          operation: "read",
          resource: "pricing"
        }
      },
      component: () => import("../views/PricingDetailsView.vue")
    },
    {
      path: "/scoring",
      name: "Scoring",
      meta: {
        permission: {
          operation: "read",
          resource: "scoring"
        }
      },
      component: () => import("../views/ScoringView.vue")
    },
    {
      path: "/scoring/:id",
      name: "ScoringDetails",
      meta: {
        permission: {
          operation: "read",
          resource: "scoring"
        }
      },
      component: () => import("../views/ScoringDetailsView.vue")
    },
    {
      path: "/sign-in",
      alias: "/",
      name: "SignIn",
      component: () => import("../views/SignInView.vue"),
      meta: { requiresRecaptcha: true }
    },
    {
      path: "/sign-up",
      name: "SignUp",
      component: () => import("../views/SignUpView.vue"),
      meta: { requiresRecaptcha: true }
    },
    {
      path: "/federated-access/:provider",
      name: "FederatedAccess",
      component: () => import("../views/FederatedAccessView.vue"),
      meta: { requiresRecaptcha: true }
    },
    {
      path: "/federated-sign-in/:provider",
      name: "FederatedSignIn",
      component: () => import("../views/FederatedSignInView.vue"),
      meta: { requiresRecaptcha: true }
    },
    {
      path: "/federated-sign-up/:provider",
      name: "FederatedSignUp",
      component: () => import("../views/FederatedSignUpView.vue"),
      meta: { requiresRecaptcha: true }
    },
    {
      path: "/connector/callback/:provider",
      name: "ConnectorCallback",
      component: () => import("../views/ConnectorCallbackView.vue"),
      meta: { requiresRecaptcha: true }
    },
    {
      path: "/forgot-password",
      name: "ForgotPassword",
      component: () => import("../views/ForgotPasswordView.vue"),
      meta: { requiresRecaptcha: true }
    },
    {
      path: "/capacity",
      name: "Capacity",
      component: () => import("../views/CapacityPlanningView.vue"),
      meta: {
        requiresFeature: FeatureFlagId.CAPACITY,
        permission: {
          operation: "read",
          resource: "capacity"
        }
      }
    },
    {
      path: "/capacity/:id",
      name: "CapacityDetails",
      component: () => import("../views/CapacityPlanningDetailsView.vue"),
      meta: {
        requiresFeature: FeatureFlagId.CAPACITY,
        permission: {
          operation: "read",
          resource: "capacity"
        }
      }
    },
    {
      path: "/accounts",
      name: "Accounts",
      meta: {
        permission: {
          operation: "read",
          resource: "account"
        }
      },
      component: () => import("../views/AccountsView.vue")
    },
    {
      path: "/accounts/:id",
      name: "AccountDetails",
      meta: {
        permission: {
          operation: "read",
          resource: "account"
        }
      },
      component: () => import("../views/AccountDetailsView.vue")
    },
    {
      path: "/territory-model",
      name: "TerritoryModels",
      meta: {
        requiresFeature: FeatureFlagId.TERRITORY_MODELS,
        permission: {
          operation: "read",
          resource: "territory-model"
        }
      },
      component: () => import("../views/TerritoryModelsView.vue")
    },
    {
      path: "/territory-model/:id",
      name: "TerritoryModelDetails",
      meta: {
        permission: {
          operation: "read",
          resource: "territory-model"
        }
      },
      component: () => import("../views/TerritoryModelDetailsView.vue")
    },
    {
      path: "/territories/:territoryId",
      name: "TerritoryDetails",
      meta: {
        permission: {
          operation: "read",
          resource: "territory"
        }
      },
      component: () => import("../views/TerritoryDetailsView.vue")
    },
    {
      path: "/territory-model/:territoryModelId/person/:personId",
      name: "TerritoryModelPersonDetails",
      meta: {
        permission: {
          operation: "read",
          resource: "territory-model"
        }
      },
      component: () => import("../views/TerritoryModelPersonDetailsView.vue")
    },
    {
      path: "/settings",
      name: "Settings",
      component: () => import("../views/SettingsView.vue")
    },
    {
      path: "/billing",
      name: "Billing",
      meta: {
        permission: {
          operation: "read",
          resource: "billing-plan"
        }
      },
      component: () => import("../views/BillingView.vue")
    },
    {
      path: "/credit-usage",
      name: "CreditUsage",
      meta: {
        permission: {
          operation: "read",
          resource: "credit-log"
        }
      },
      component: () => import("../views/CreditUsageView.vue")
    },
    {
      path: "/users",
      name: "Users",
      meta: {
        permission: {
          operation: "read",
          resource: "user"
        }
      },
      component: () => import("../views/UsersView.vue")
    },
    {
      path: "/jobs",
      name: "Jobs",
      meta: {
        permission: {
          operation: "read",
          resource: "job"
        }
      },
      component: () => import("../views/JobsView.vue")
    },
    {
      path: "/organization",
      name: "Organization",
      component: () => import("../views/OrganizationView.vue"),
      meta: {
        permission: {
          operator: "OR",
          permissions: [
            {
              operation: "read",
              resource: "person"
            },
            {
              operation: "read",
              resource: "role"
            }
          ]
        }
      }
    },
    {
      path: "/forbidden",
      name: "Forbidden",
      component: () => import("../views/ForbiddenView.vue"),
      props: {}
    },
    {
      // Wildcard - redirect to Not Found page
      path: "/:pathMatch(.*)*",
      name: "NotFound",
      component: () =>
        import("../components/NotFoundComponent/NotFoundComponent.vue")
    }
  ]
});

router?.beforeEach(async (to, _, next) => {
  // Check permissions
  if (to.meta.permission) {
    const config = to.meta.permission as
      | PermissionConfig
      | PermissionConfig[]
      | PermissionConfigWithOperator;
    const permissionStore = usePermissionStore();
    const result = permissionStore.hasPermission(config);
    if (!result.allowed) {
      next(
        `/forbidden?reason=${result.denyReason}&category=${result.denyCategory}`
      );
      return;
    }
  }

  // Handle recaptcha
  if (to.meta?.requiresRecaptcha) {
    await GoogleRecaptchaHelper.insertRecaptchaScript();
  } else {
    // Hide the reCAPTCHA badge instead of removing the script/DOM.
    // Aggressive removal causes reCAPTCHA's internal timers to reject
    // promises with the bare string "Timeout" (unhandled rejection).
    const badges = document.querySelectorAll(".grecaptcha-badge");
    badges.forEach(
      (badge) => ((badge as HTMLElement).style.visibility = "hidden")
    );
  }

  // Check feature flags
  if (to.meta?.requiresFeature) {
    const featureFlagStore = useFeatureFlagStore();
    // Ensure features are loaded
    if (!featureFlagStore.isLoaded) {
      await featureFlagStore.loadFeatureFlags();
    }

    const featureId = to.meta.requiresFeature as FeatureFlagId;
    if (!featureFlagStore.isFeatureEnabled(featureId)) {
      // Redirect to home if feature is not enabled
      next({ name: "Home" });
      return;
    }
  }

  next();
});

export default router;
